/**
 * formatters.ts — render cards, CKO envelopes, tables, error coaching.
 *
 * Phase CG. The CLI's "visual DNA": liquid-glass cards, category colour
 * coding, JetBrains Mono. Every renderer produces a DOM fragment that the
 * runner appends to the feed.
 *
 * Locked vocabulary applies: 1 Zeqond = 0.777 s. Never write "seconds" or
 * "ms" on user surfaces — show Zeqonds (Z) and millizeqonds (mz).
 */

export type Category =
  | "compute" | "contract" | "chain" | "agent" | "wallet"
  | "identity" | "pulse" | "site" | "skill" | "api"
  | "system" | "tutorial";

export const CATEGORY_COLOR: Record<Category, string> = {
  compute:  "#00d4ff",
  contract: "#00ff88",
  chain:    "#c084fc",
  agent:    "#fbbf24",
  wallet:   "#eab308",
  identity: "#60a5fa",
  pulse:    "#34d399",
  site:     "#f472b6",
  skill:    "#a78bfa",
  api:      "#22d3ee",
  system:   "#9ca3af",
  tutorial: "#00ff88",
};

/** Escape HTML for safe innerHTML. */
export function esc(s: unknown): string {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** A liquid-glass card with a category colour bar.
 *
 *  Phase CI.4 — `learnMoreHtml` (optional 5th arg) appends a collapsible
 *  educational drop-down rendered by learn-more.ts → renderLearnMore(head).
 *  Backward compatible — existing callers pass 3 or 4 args and get the
 *  same DOM. */
export function card(
  category: Category,
  titleHtml: string,
  bodyHtml: string,
  hintHtml?: string,
  learnMoreHtml?: string,
): HTMLElement {
  const colour = CATEGORY_COLOR[category];
  const el = document.createElement("div");
  el.className = "cli-card cli-card-" + category;
  el.style.borderColor = "rgba(0,255,136,.18)";
  el.style.setProperty("--cli-cat-color", colour);
  el.innerHTML =
    '<div class="cli-card-head">' +
      '<span class="cli-card-tag" style="color:' + colour + ';border-color:' + colour + ';">' + esc(category.toUpperCase()) + '</span>' +
      '<span class="cli-card-title">' + titleHtml + '</span>' +
    '</div>' +
    '<div class="cli-card-body">' + bodyHtml + '</div>' +
    (hintHtml ? '<div class="cli-card-hint">' + hintHtml + '</div>' : '') +
    (learnMoreHtml || '');
  return el;
}

/** A plain line with a kind class. */
export function line(text: string, kind: "user" | "ok" | "err" | "dim" = "ok"): HTMLElement {
  const el = document.createElement("div");
  el.className = "cli-line " + kind;
  el.textContent = text;
  return el;
}

/** A bordered table: rows = array of arrays of HTML strings. */
export function table(headers: string[], rows: string[][]): HTMLElement {
  const el = document.createElement("div");
  el.className = "cli-table";
  let html = '<div class="cli-table-row cli-table-head">';
  for (const h of headers) html += '<div class="cli-table-cell">' + esc(h) + '</div>';
  html += '</div>';
  for (const row of rows) {
    html += '<div class="cli-table-row">';
    for (const c of row) html += '<div class="cli-table-cell">' + c + '</div>';
    html += '</div>';
  }
  el.innerHTML = html;
  return el;
}

/**
 * CKO envelope renderer. Accepts the response from /api/zeq/compute and
 * produces the developer-grade proof card the framework prides itself on.
 */
export interface ComputeResponse {
  value?: number;
  unit?: string;
  units?: string;
  operators?: string[];
  domain?: string;
  zeqond?: number;
  phase?: number;
  zeqProof?: string;
  zeq_proof?: string;
  proof?: string;
  precision?: number;
  ko42_carrier?: number | string;
  /** Phase CH (2026-05-15) — path-relative URL to the per-tick explorer page,
   *  e.g. "/state/detail.html?slug=<machine>&zeqond=<n>". The compute endpoint
   *  derives this server-side via getCallerStateMachine() + the result zeqond. */
  explorer_url?: string;
  charge?: { total?: number; burned?: number; foundation?: number; remaining?: number };
  tally_charge?: { total?: number; burned?: number; foundation?: number; remaining?: number };
  meta?: Record<string, unknown>;
  envelope?: Record<string, unknown>;
  [k: string]: unknown;
}

/**
 * Phase CI.4 — `ckoCard` now accepts an optional `inputs` map so it can
 * surface a "Push to contract" action. Clicking it builds a deployable
 * contract definition for the same operator + inputs (every-N-Zeqonds
 * trigger, default 60Z), copies it to the clipboard, and switches the
 * workbench to the Queries tab so the user can paste + tune.
 */
export function ckoCard(opLabel: string, r: ComputeResponse, inputs?: Record<string, number>): HTMLElement {
  // Phase CH (2026-05-15) — the wizard response nests zeqond/phase/domain
  // under `r.zeqState`, not at top-level. Fall through both shapes so old
  // and new responses render correctly.
  const ze = (r as { zeqState?: { zeqond?: number; phase?: number; domain?: string; operators?: string[] } }).zeqState ?? {};
  const value = r.value;
  const units = r.unit ?? r.units ?? "";
  const proof = (r.zeqProof ?? r.zeq_proof ?? r.proof ?? "") as string;
  const ops = Array.isArray(r.operators) && r.operators.length
    ? r.operators.join(" · ")
    : Array.isArray(ze.operators) && ze.operators.length
      ? ze.operators.join(" · ")
      : opLabel;
  const domain = r.domain ?? ze.domain ?? "—";
  const zeqondVal: number | string | undefined = r.zeqond ?? ze.zeqond;
  const zeqond = (typeof zeqondVal === "number" && isFinite(zeqondVal)) ? Math.floor(zeqondVal) : (zeqondVal ?? "—");
  const phaseVal = typeof r.phase === "number" ? r.phase : (typeof ze.phase === "number" ? ze.phase : undefined);
  const phase = typeof phaseVal === "number" ? phaseVal.toFixed(6) : "—";
  const precision = typeof r.precision === "number" ? (r.precision * 100).toFixed(4) + "%" : "≤0.1%";
  const carrier = r.ko42_carrier ?? "KO42 applied";
  const charge = r.charge ?? r.tally_charge;
  const valueText = (value !== undefined && value !== null)
    ? '<span class="cli-cko-value">' + esc(String(value)) + '</span> <span class="cli-cko-units">' + esc(units) + '</span>'
    : '<span class="cli-cko-value dim">—</span>';

  const rows: string[] = [];
  const add = (k: string, v: string) => rows.push(
    '<div class="cli-cko-row"><span class="cli-cko-k">' + esc(k) + '</span><span class="cli-cko-v">' + v + '</span></div>'
  );
  add("operators",     '<code>' + esc(ops) + '</code>');
  add("domain",        '<code>' + esc(domain) + '</code>');
  add("KO42 carrier",  '<code>' + esc(String(carrier)) + '</code>');
  add("Zeqond",        '<code>' + esc(String(zeqond)) + '</code> <span class="dim">(τ = 0.777 s)</span>');
  add("phase",         '<code>' + esc(String(phase)) + '</code>');
  add("precision",     '<code>' + esc(precision) + '</code> <span class="dim">deviation</span>');
  if (proof) add("ZeqProof", '<code>' + esc(proof.slice(0, 24)) + (proof.length > 24 ? '…' : '') + '</code>');
  if (charge && typeof charge.total === "number") {
    add("charge", '<code>' + esc(String(charge.total)) + ' credits</code> <span class="dim">foundation ' + esc(String(charge.foundation ?? '?')) + '</span>');
  }
  // Phase CH — every transition is anchored to the entangled-state explorer.
  // The backend returns a path-relative URL; we render it as a clickable link
  // that opens /state/detail.html?slug=<m>&zeqond=<n> in a new tab.
  const explorerUrl = typeof r.explorer_url === "string" ? r.explorer_url : "";
  if (explorerUrl) {
    add("explorer",
      '<a href="' + esc(explorerUrl) + '" target="_blank" rel="noopener" class="cli-cko-explorer-link">' +
        '<code>' + esc(explorerUrl) + '</code>' +
        '<span class="cli-cko-explorer-arrow">↗</span>' +
      '</a>');
  }

  // Phase CI.4 — close-the-loop "push to contract" action.
  // Renders a button into the card body that, when clicked, builds a
  // deployable contract definition for this exact op+inputs, copies it
  // to the clipboard, and dispatches an event the Queries tab listens
  // for so the user can paste+tune immediately.
  const pushBtnHtml = inputs
    ? '<div class="cli-cko-push-row"><button class="cli-cko-push-btn" data-op="' + esc(opLabel) + '" data-inputs="' + esc(JSON.stringify(inputs)) + '">↳ push this into a contract</button>' +
      '<span class="cli-cko-push-hint">make this fire every 60 Zeqonds · entangled-state-logged · webhook-able</span></div>'
    : '';

  const el = card(
    "compute",
    '<span class="cli-cko-head-op">' + esc(opLabel) + '</span> &nbsp;→&nbsp; ' + valueText,
    rows.join("") + pushBtnHtml,
    'CKO envelope · master-equation receipt · reproducible against NIST CODATA 2018',
  );

  // Wire the push-button after the card is built (querySelector inside).
  if (inputs) {
    const btn = el.querySelector(".cli-cko-push-btn") as HTMLButtonElement | null;
    if (btn) {
      btn.addEventListener("click", (ev) => {
        ev.preventDefault();
        const op = btn.getAttribute("data-op") || opLabel;
        let parsedInputs: Record<string, number> = inputs;
        try {
          parsedInputs = JSON.parse(btn.getAttribute("data-inputs") || "{}");
        } catch { /* fall back to closure inputs */ }
        const contractDef = {
          name: "compute_" + op.toLowerCase() + "_60z",
          description: "Auto-generated from a CLI compute. Fires every 60 Zeqonds (~46.6 s) and runs " + op + " with the same inputs.",
          transitions: [
            {
              from: "ready",
              to: "ready",
              trigger: { kind: "every", every: 60 },
              compute: { operators: [op], inputs: parsedInputs },
            },
          ],
        };
        const json = JSON.stringify(contractDef, null, 2);
        try {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            void navigator.clipboard.writeText(json);
          }
        } catch { /* clipboard may be blocked — non-fatal */ }
        try {
          window.dispatchEvent(new CustomEvent("zeq:contract:draft", { detail: contractDef }));
        } catch { /* */ }
        btn.textContent = "✓ contract JSON copied · pasting in Queries tab…";
        btn.classList.add("done");
        btn.disabled = true;
      });
    }
  }

  return el;
}

/** Format a Zeqond float with one decimal — locked vocab (never "seconds"). */
export function fmtZ(z: number | string | null | undefined): string {
  if (z == null) return "—";
  const n = typeof z === "string" ? parseFloat(z) : z;
  if (!isFinite(n)) return "—";
  return n.toFixed(1) + " Z";
}

/** Levenshtein distance for "did you mean?" coaching. */
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  const m = a.length, n = b.length;
  if (!m) return n;
  if (!n) return m;
  const dp: number[] = new Array(n + 1);
  for (let j = 0; j <= n; j++) dp[j] = j;
  for (let i = 1; i <= m; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const tmp = dp[j];
      dp[j] = a[i - 1] === b[j - 1]
        ? prev
        : 1 + Math.min(prev, dp[j], dp[j - 1]);
      prev = tmp;
    }
  }
  return dp[n];
}

/** Find the closest match from a list, max edit distance 3. */
export function closest(input: string, candidates: string[]): string | null {
  let best: string | null = null;
  let bestD = 4;
  const inLow = input.toLowerCase();
  for (const c of candidates) {
    const d = levenshtein(inLow, c.toLowerCase());
    if (d < bestD) { bestD = d; best = c; }
  }
  return best;
}
